import React from "react"
import {navigate} from "gatsby"
import PropTypes from "prop-types"
import Styled from "styled-components"
import {getFixedGatsbyImage} from "gatsby-storyblok-image"
import Img from "gatsby-image"
import ReactMarkdown from "react-markdown"
import {Col} from "@styles/grid"
import {Button} from "@uprise/button"
import {Heading2, Heading3, BodyText, Note, ContentWrapper} from "@styles/text"
import {primary, extended} from "@constants/colors"
import {getDimensions, useContainerWidth} from "@utils"

const SingleMediaWithCaptionAndParagraphWrapper = Styled.div`
  flex-direction: ${props => (props.textFirst ? "row-reverse" : "row")};
  align-items: center;
  & img {
    width: 100%;
    max-width: ${props => (props.timeline ? `${props.width / 2}px` : "inherit")};
    max-height: ${props => (props.timeline ? `${props.height / 2}px` : "inherit")};
  }

  & .media {
    width: 100%;
    display: flex;
    justify-content: center;
    margin: auto;
    margin-bottom: 32px;
    position: relative;

    & .oval {
      position: absolute;
      /* height: 200%; */
      max-height: 800px;
      top: -50%;
      left: ${props => (props.textFirst ? "inherit" : "-50%")};
      right: ${props => (props.textFirst ? "-50%" : "inherit")};
      transform: ${props => (props.textFirst ? "none" : "rotate(180deg)")};
    }

    & .gatsby-image-wrapper {
      width: 100%;
    }

    @media (min-width: 1024px) {
      margin-bottom: 0px;
    }
  }

  & .info:before {
    display: none;
    content: '';
    width: 16px;
    height: 16px;
    background-color: ${primary.purple};
    position: absolute;
    border-radius: 50%;
    left: ${props => (!props.textFirst ? "-16px" : "inherit")};
    right: ${props => (props.textFirst ? "0" : "inherit")};
    top: 10px;

    @media (min-width: 768px) {
      display: ${props => (props.timeline ? "block" : "none")};
    }
  }

  & .info {
    position: relative;

    & .step {
      font-weight: 600;
      color: ${extended.charcoal.two};
    }

    & p {
      margin-bottom: 10px;
    }

    & .heading {
      text-align: left;
      margin-bottom: 16px;
    }
    @media (min-width: 768px) {
      padding-left: ${props => (props.textFirst ? "0px" : "50px")};
    }
  }
`

const BodyWithHTML = Styled(BodyText)`
  & span {
    font-family: 'Proxima Nova Semibold';
    color: ${props => props.highlightColor};
  }
`

function SingleMediaWithCaptionAndParagraph({
	title,
	body,
	stepNumber,
	showStepNumber,
	media,
	timeline,
	content,
	highlightColor,
	actions,
	marginTopEnabled,
	showOval = false
}) {
	const containerWidth = useContainerWidth(".SingleMediaWithCaptionAndParagraphWrapper")

	const [width, height] = getDimensions(media)
	const aspectRatio = width / height
	const adjustedWidth = aspectRatio < 1.1 ? Math.round(containerWidth * 0.75) : containerWidth
	const fixedProps = getFixedGatsbyImage(media, {
		width: adjustedWidth,
		toFormat: "webp"
	})

	const Heading = showStepNumber ? Heading2 : Heading3

	return (
		<SingleMediaWithCaptionAndParagraphWrapper
			className='row'
			textFirst={stepNumber % 2 === 0}
			timeline={timeline}
			showOval={showOval}
			width={width}
			height={height}>
			<Col className='col-12 col-lg-6 align-self-center SingleMediaWithCaptionAndParagraphWrapper'>
				<div className='media'>
					{showOval && <img className='oval' src={require("../../images/semi-circle.svg")} alt='' />}
					{fixedProps && <Img fixed={fixedProps} alt={title} fadeIn={false} loading='eager' />}
				</div>
			</Col>
			<Col className='col-lg-6 align-self-center info'>
				{stepNumber && showStepNumber && <Note className='step'>{`STEP ${stepNumber}`}</Note>}
				<Heading className='heading'>{title}</Heading>
				<BodyWithHTML highlightColor={highlightColor} dangerouslySetInnerHTML={{__html: body}}></BodyWithHTML>
				{content && (
					<ContentWrapper contained>
						<ReactMarkdown source={content} />
					</ContentWrapper>
				)}

				{actions &&
					actions.map(action => {
						return (
							<Button
								key={action._uid}
								className={`action ${marginTopEnabled ? "m-t-4" : ""}`}
								{...action}
								onClick={() => navigate(action.link)}></Button>
						)
					})}
			</Col>
		</SingleMediaWithCaptionAndParagraphWrapper>
	)
}

export default SingleMediaWithCaptionAndParagraph

SingleMediaWithCaptionAndParagraph.propTypes = {
	title: PropTypes.string.isRequired
}
SingleMediaWithCaptionAndParagraph.defaultTypes = {
	title: PropTypes.string.isRequired
}
